import React, { useState, useEffect } from "react";
import tw from "twin.macro";
import styled from "styled-components";
import { css } from "styled-components/macro"; //eslint-disable-line
import { useLocation } from "react-router-dom";

import Header, {
  NavLink,
  NavLinks,
  PrimaryLink,
  LogoLink,
  NavToggle,
  DesktopNavLinks,
  SecondaryLink,
} from "../headers/light.js";
import ResponsiveVideoEmbed from "../../helpers/ResponsiveVideoEmbed.js";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface HeroButton {
  text: string;
  href: string;
}

export interface HeroProps {
  /** URL de l'image de fond. Si absent, backgroundCss est utilisé. */
  backgroundImageSrc?: string;
  /** CSS background fallback : couleur solide ou dégradé (ex. "#111" ou "linear-gradient(...)") */
  backgroundCss?: string;
  /** Opacité de l'overlay sombre. 0 = aucun overlay. Default 0.5 */
  overlayOpacity?: number;
  /** Petite ligne au-dessus du titre */
  notification?: React.ReactNode;
  /** Titre principal (obligatoire) */
  heading: React.ReactNode;
  /** Description sous le titre */
  description?: React.ReactNode;
  /** Bouton CTA principal */
  primaryButton?: HeroButton;
  /** Bouton CTA secondaire */
  secondaryButton?: HeroButton;
  /**
   * Contenu affiché dans la colonne droite (vidéo, image, etc.).
   * Si absent, la colonne gauche est centrée et prend toute la largeur.
   */
  rightContent?: React.ReactNode;
  /** Contenu extra rendu sous les boutons (ex : onglets de destination) */
  extraContent?: React.ReactNode;
}

// ─── Styled ──────────────────────────────────────────────────────────────────

const FixedHeaderWrapper = styled.div<{ $scrolled: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 50;
  transition:
    background-color 0.4s ease,
    box-shadow 0.4s ease,
    padding 0.4s ease;
  background-color: ${(p) => (p.$scrolled ? "white" : "transparent")};
  box-shadow: ${(p) => (p.$scrolled ? "0 2px 24px rgba(0,0,0,0.10)" : "none")};
  padding: ${(p) => (p.$scrolled ? "1.3rem 5rem" : "3rem 4rem")};

  @media (max-width: 1024px) {
    padding: ${(p) => (p.$scrolled ? "1rem 1.5rem" : "1.5rem 1.5rem")};
  }
`;

/**
 * Lien de navigation avec état actif (orange).
 * Le triple && donne une spécificité 0,3,0 > 0,2,0 des overrides !important
 * de ScrollStyledHeader.
 */
const ActiveNavLink = styled(NavLink)<{ $active: boolean }>`
  ${(p) =>
    p.$active &&
    `&&& {
      color: #ff6c00 !important;
      border-color: #ff6c00 !important;
      border-bottom-width: 2px;
    }`}
`;

const ScrollStyledHeader = styled(Header)<{ $scrolled: boolean }>`
  ${tw`max-w-none`}
  ${DesktopNavLinks} ${NavLink}, ${LogoLink} {
    color: ${(p) => (p.$scrolled ? "#111111" : "#f7fafc")} !important;
    border-color: transparent;
    transition:
      color 0.4s ease,
      border-color 0.3s ease;
    &:hover {
      color: #ff6c00 !important;
      border-color: #ff6c00;
    }
  }
  ${DesktopNavLinks} ${PrimaryLink},
  ${DesktopNavLinks} ${SecondaryLink} {
    color: #f7fafc !important;
    &:hover {
      color: #f7fafc !important;
      border-color: transparent;
    }
  }
  ${NavToggle}.closed {
    color: ${(p) => (p.$scrolled ? "#111111" : "#f7fafc")};
    transition: color 0.4s ease;
  }
`;

const Container = styled.div<{ $bg?: string }>`
  ${tw`relative -mx-8 -mt-8 bg-center bg-cover`}
  min-height: 60vh;
  ${(p) => p.$bg && `background: ${p.$bg};`}
`;

const BgImage = styled.div<{ $src: string }>`
  ${tw`absolute inset-0 bg-center bg-cover`}
  background-image: url("${(p) => p.$src}");
`;

const OpacityOverlay = styled.div<{ $opacity: number }>`
  ${tw`z-10 absolute inset-0 bg-secondary-500`}
  opacity: ${(p) => p.$opacity};
`;

const HeroContainer = tw.div`z-20 relative px-4 sm:px-8 max-w-screen-xl mx-auto`;

const TwoColumn = styled.div<{ $hasRight: boolean }>`
  ${tw`pt-40 pb-32 px-4 flex items-center flex-col`}
  ${(p) =>
    p.$hasRight
      ? tw`lg:flex-row justify-between`
      : tw`lg:flex-col justify-center`}
`;

const LeftColumn = styled.div<{ $hasRight: boolean }>`
  ${(p) =>
    p.$hasRight
      ? tw`flex flex-col items-center lg:items-start`
      : tw`flex flex-col items-center text-center max-w-3xl w-full`}
`;

const RightColumn = tw.div`w-full sm:w-5/6 lg:w-1/2 mt-16 lg:mt-0 lg:pl-8`;

const Notification = tw.span`inline-block my-4 pl-3 py-1 text-gray-100 border-l-4 border-primary-500 font-medium text-sm`;

const Heading = styled.h1<{ $centered: boolean }>`
  ${tw`text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-black text-gray-100 leading-none`}
  ${(p) => (p.$centered ? tw`text-center` : tw`text-center lg:text-left`)}
  span {
    ${tw`inline-block mt-2`}
  }
`;

/** Fond blanc incliné derrière un mot-clé dans le titre */
export const SlantedBackground = styled.span`
  ${tw`relative text-primary-500 px-4 -mx-4 py-2`}
  &::before {
    content: "";
    ${tw`absolute inset-0 bg-gray-100 transform -skew-x-12 -z-10`}
  }
`;

const Description = styled.p<{ $centered: boolean }>`
  ${tw`mt-6 text-gray-300 text-base md:text-lg leading-relaxed max-w-xl`}
  ${(p) => p.$centered && tw`text-center mx-auto`}
`;

const ButtonsRow = styled.div<{ $centered: boolean }>`
  ${tw`flex flex-wrap gap-4 mt-8`}
  ${(p) => p.$centered && tw`justify-center`}
`;

const HeroPrimaryBtn = styled.a`
  ${tw`px-8 py-4 bg-primary-500 text-gray-100 font-bold shadow no-underline inline-flex items-center gap-2 transition duration-300`}
  &:hover {
    background: #f7fafc;
    color: #ff6c00;
  }
`;

const HeroSecondaryBtn = styled.a`
  ${tw`px-8 py-4 bg-transparent text-gray-100 font-bold border-2 border-white no-underline inline-flex items-center gap-2 transition duration-300`}
  &:hover {
    background: white;
    color: #111111;
  }
`;

const ExtraContentWrap = tw.div`mt-10 w-full`;

/** Wrapper réutilisable pour l'intégration vidéo (rightContent de HomePage) */
export const StyledResponsiveVideoEmbed = styled(ResponsiveVideoEmbed)`
  padding-bottom: 56.25% !important;
  padding-top: 0px !important;
  ${tw`rounded`}
  iframe {
    ${tw`rounded bg-black shadow-xl`}
  }
`;

// ─── Liens de navigation ──────────────────────────────────────────────────────

const NAV_ITEMS = [
  { href: "/", label: "Accueil" },
  { href: "/services", label: "Nos services" },
  { href: "/destinations", label: "Destinations" },
  { href: "/contact", label: "Nous contacter" },
] as const;

// ─── Composant ────────────────────────────────────────────────────────────────

const BackgroundAsImageHero: React.FC<HeroProps> = ({
  backgroundImageSrc,
  backgroundCss,
  overlayOpacity = 0.5,
  notification,
  heading,
  description,
  primaryButton,
  secondaryButton,
  rightContent,
  extraContent,
}) => {
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  /** true si le lien href correspond au chemin actuel */
  const isActive = (href: string) =>
    href === "/" ? pathname === "/" : pathname.startsWith(href);

  const navLinks = [
    <NavLinks key={1}>
      {NAV_ITEMS.map(({ href, label }) => (
        <ActiveNavLink key={href} href={href} $active={isActive(href)}>
          {label}
        </ActiveNavLink>
      ))}
    </NavLinks>,
    <NavLinks key={2} tw="flex items-center gap-4">
      <SecondaryLink href="/login">Mon espace</SecondaryLink>
      <PrimaryLink href="/verifier-avi">Vérifier un document</PrimaryLink>
    </NavLinks>,
  ];

  const hasRight = !!rightContent;
  const bgFallback = backgroundCss ?? "#111111";

  return (
    <>
      <FixedHeaderWrapper $scrolled={scrolled}>
        <ScrollStyledHeader
          links={navLinks}
          logoLink={null}
          className=""
          $scrolled={scrolled}
        />
      </FixedHeaderWrapper>

      <Container $bg={!backgroundImageSrc ? bgFallback : undefined}>
        {backgroundImageSrc && <BgImage $src={backgroundImageSrc} />}
        {backgroundImageSrc && overlayOpacity > 0 && (
          <OpacityOverlay $opacity={overlayOpacity} />
        )}

        <HeroContainer>
          <TwoColumn $hasRight={hasRight}>
            <LeftColumn $hasRight={hasRight}>
              {notification && <Notification>{notification}</Notification>}

              <Heading $centered={!hasRight}>{heading}</Heading>

              {description && (
                <Description $centered={!hasRight}>{description}</Description>
              )}

              {(primaryButton || secondaryButton) && (
                <ButtonsRow $centered={!hasRight}>
                  {primaryButton && (
                    <HeroPrimaryBtn href={primaryButton.href}>
                      {primaryButton.text}
                    </HeroPrimaryBtn>
                  )}
                  {secondaryButton && (
                    <HeroSecondaryBtn href={secondaryButton.href}>
                      {secondaryButton.text}
                    </HeroSecondaryBtn>
                  )}
                </ButtonsRow>
              )}

              {extraContent && (
                <ExtraContentWrap>{extraContent}</ExtraContentWrap>
              )}
            </LeftColumn>

            {hasRight && <RightColumn>{rightContent}</RightColumn>}
          </TwoColumn>
        </HeroContainer>
      </Container>
    </>
  );
};

export default BackgroundAsImageHero;
